import { useCallback, useEffect, useRef, useState, type RefObject, type UIEvent } from 'react';

import {
  expandBoardExtentForProjectedScroll,
  expandBoardExtentNearScrollEdges,
  getMinimumBoardExtent,
  growBoardExtentToMinimum,
  type BoardExtent,
} from './boardExtent';

export function useExpandableBoardCanvas(
  viewportRef: RefObject<HTMLElement | null>,
  spacerRef: RefObject<HTMLElement | null>,
  contentExtent: BoardExtent
) {
  const edgeExpansionFrameRef = useRef<number | null>(null);
  const pendingEdgeExpansionViewportRef = useRef<HTMLElement | null>(null);
  const wheelExpansionFrameRef = useRef<number | null>(null);
  const pendingWheelDeltaRef = useRef({ x: 0, y: 0 });
  const extentRef = useRef<BoardExtent>(getMinimumBoardExtent(contentExtent, getWindowExtent()));
  const [minimumCanvasSize, setMinimumCanvasSize] = useState<BoardExtent>(() => extentRef.current);

  const setCanvasExtent = useCallback(
    (nextExtent: BoardExtent) => {
      extentRef.current = nextExtent;

      const viewport = viewportRef.current;
      const spacer = spacerRef.current;

      if (spacer) {
        const scrollLeft = viewport?.scrollLeft;
        const scrollTop = viewport?.scrollTop;

        spacer.style.minHeight = `${nextExtent.height}px`;
        spacer.style.minWidth = `${nextExtent.width}px`;

        if (viewport && scrollLeft != null && scrollTop != null) {
          viewport.scrollLeft = scrollLeft;
          viewport.scrollTop = scrollTop;
        }
      }
    },
    [spacerRef, viewportRef]
  );

  useEffect(() => {
    const viewport = viewportRef.current;

    const updateMinimumExtent = () => {
      const viewportExtent = viewport
        ? { height: viewport.clientHeight, width: viewport.clientWidth }
        : getWindowExtent();
      const minimumExtent = getMinimumBoardExtent(contentExtent, viewportExtent);

      const nextExtent = growBoardExtentToMinimum(extentRef.current, minimumExtent);
      setCanvasExtent(nextExtent);
      setMinimumCanvasSize((currentMinimumSize) =>
        currentMinimumSize.height === minimumExtent.height &&
        currentMinimumSize.width === minimumExtent.width
          ? currentMinimumSize
          : minimumExtent
      );
    };

    updateMinimumExtent();
    if (!viewport) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateMinimumExtent);
    resizeObserver.observe(viewport);

    return () => {
      resizeObserver.disconnect();
    };
  }, [contentExtent, setCanvasExtent, viewportRef]);

  const expandCanvasNearViewportEdges = useCallback(
    (viewport = viewportRef.current) => {
      if (!viewport) {
        return;
      }

      pendingEdgeExpansionViewportRef.current = viewport;

      if (edgeExpansionFrameRef.current != null) {
        return;
      }

      edgeExpansionFrameRef.current = requestAnimationFrame(() => {
        edgeExpansionFrameRef.current = null;
        const pendingViewport = pendingEdgeExpansionViewportRef.current;
        pendingEdgeExpansionViewportRef.current = null;

        if (!pendingViewport) {
          return;
        }

        const nextExtent = expandBoardExtentNearScrollEdges(extentRef.current, {
          clientHeight: pendingViewport.clientHeight,
          clientWidth: pendingViewport.clientWidth,
          scrollHeight: pendingViewport.scrollHeight,
          scrollLeft: pendingViewport.scrollLeft,
          scrollTop: pendingViewport.scrollTop,
          scrollWidth: pendingViewport.scrollWidth,
        });

        if (nextExtent !== extentRef.current) {
          setCanvasExtent(nextExtent);
        }
      });
    },
    [setCanvasExtent, viewportRef]
  );

  useEffect(
    () => () => {
      if (edgeExpansionFrameRef.current != null) {
        cancelAnimationFrame(edgeExpansionFrameRef.current);
        edgeExpansionFrameRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return undefined;
    }

    const preventHorizontalBrowserNavigation = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        return;
      }

      event.preventDefault();
      viewport.scrollLeft += getWheelDeltaPixels(
        event.deltaX,
        event.deltaMode,
        viewport.clientWidth
      );
      viewport.scrollTop += getWheelDeltaPixels(
        event.deltaY,
        event.deltaMode,
        viewport.clientHeight
      );
    };

    const scheduleWheelExpansion = (event: WheelEvent) => {
      const deltaX = getWheelDeltaPixels(event.deltaX, event.deltaMode, viewport.clientWidth);
      const deltaY = getWheelDeltaPixels(event.deltaY, event.deltaMode, viewport.clientHeight);
      pendingWheelDeltaRef.current = {
        x: pendingWheelDeltaRef.current.x + deltaX,
        y: pendingWheelDeltaRef.current.y + deltaY,
      };

      if (wheelExpansionFrameRef.current != null) {
        return;
      }

      wheelExpansionFrameRef.current = requestAnimationFrame(() => {
        wheelExpansionFrameRef.current = null;
        const pendingWheelDelta = pendingWheelDeltaRef.current;
        pendingWheelDeltaRef.current = { x: 0, y: 0 };

        const nextExtent = expandBoardExtentForProjectedScroll(
          extentRef.current,
          getViewportMeasurements(viewport),
          {
            deltaX: pendingWheelDelta.x,
            deltaY: pendingWheelDelta.y,
          }
        );

        if (nextExtent !== extentRef.current) {
          setCanvasExtent(nextExtent);
        }
      });
    };

    viewport.addEventListener('wheel', preventHorizontalBrowserNavigation, { passive: false });
    viewport.addEventListener('wheel', scheduleWheelExpansion, { passive: true });

    return () => {
      if (wheelExpansionFrameRef.current != null) {
        cancelAnimationFrame(wheelExpansionFrameRef.current);
        wheelExpansionFrameRef.current = null;
      }
      pendingWheelDeltaRef.current = { x: 0, y: 0 };

      viewport.removeEventListener('wheel', preventHorizontalBrowserNavigation);
      viewport.removeEventListener('wheel', scheduleWheelExpansion);
    };
  }, [setCanvasExtent, viewportRef]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      expandCanvasNearViewportEdges(event.currentTarget);
    },
    [expandCanvasNearViewportEdges]
  );

  return { canvasSize: minimumCanvasSize, expandCanvasNearViewportEdges, handleScroll };
}

function getWindowExtent(): BoardExtent {
  if (typeof window === 'undefined') {
    return { height: 0, width: 0 };
  }

  return { height: window.innerHeight, width: window.innerWidth };
}

function getViewportMeasurements(viewport: HTMLElement) {
  return {
    clientHeight: viewport.clientHeight,
    clientWidth: viewport.clientWidth,
    scrollHeight: viewport.scrollHeight,
    scrollLeft: viewport.scrollLeft,
    scrollTop: viewport.scrollTop,
    scrollWidth: viewport.scrollWidth,
  };
}

function getWheelDeltaPixels(delta: number, deltaMode: number, viewportSize: number) {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return delta * 16;
  }

  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return delta * viewportSize;
  }

  return delta;
}
